<template>
	<details v-for="(item, index) in props.items" :key="index" class="c_details listItem" @toggle="onClickToggle">
		<summary class="listItem_summary">
			<div class="listItem_content">
				<div class="bookmark">
					<IconStar
						:icon-color="!item.bookmark ? '#D9D9D9' : '#FFB800'"
						@click.prevent
						@click.stop="uploadBookMark(item.event_pk, item.bookmark)"
					/>
				</div>
				<div class="c_inner">
					<div class="item_pick">
						<img :src="item.file_path ?? logoImg" alt="기업 로고" loading="lazy" />
						<div class="item_title">
							<span v-if="item.event_name !== '비공개'">{{ item.event_name }}</span>
							<span v-else class="clip">
								<IconClip v-for="num in 5" :key="num" alt="종목명 가림 아이콘" />
							</span>
							<span v-if="item.event_name !== '비공개'">{{ item.event_ticker || item.event_code }}</span>
						</div>
					</div>
					<div class="item_total">{{ item.event_score }}점</div>
				</div>
			</div>
		</summary>
		<ul class="listItem_bottom">
			<li v-for="(item_sub, index_sub) in item.master" :key="index_sub" class="c_editorContent listItem_bottom_content">
				<div class="content_master">
					<img :src="item_sub.file_path" alt="대가이미지" loading="lazy" />
					<p>{{ item_sub.master_name }}</p>
				</div>
			</li>
			<div class="btn_box">
				<button
					class="c_button primary-fill radius-full height-small btn-research"
					@click="onClickResearch(item.event_pk)"
				>
					분석
				</button>
			</div>
		</ul>
	</details>
</template>

<script setup lang="ts">
// core
import { useRouter } from 'vue-router';

// api
import { HotRankingItem } from '@/api/hotRanking';
import { useAdjustBookmarksApi } from '@/api/Interest';
import logoImg from '@/assets/img/bi/bi.svg';
// store
import { useTokenStore } from '@/stores/token';
// util
import { checkLoginDetail } from '@/utils/function/login';

// atom
import IconClip from '../atom/icon/IconClip.vue';
import IconStar from '../atom/icon/IconStar.vue';

// -----variable-----
const props = defineProps<{
	items?: HotRankingItem[];
}>();
const emit = defineEmits(['update:bookMarkRefetch']);

const router = useRouter();
const tokenStore = useTokenStore();

// -----api-----
const { mutateAsync: adjustBookmarks } = useAdjustBookmarksApi();

// -----function-----
// 다중 열림 방지 함수
const onClickToggle = (e: Event) => {
	const toggled = e.target as HTMLDetailsElement;
	if (toggled && toggled.hasAttribute('open')) {
		toggled.classList.add('open');
		document.querySelectorAll('details[open]').forEach(function (opened) {
			if (toggled !== opened) {
				(opened as HTMLDetailsElement).removeAttribute('open');
				(opened as HTMLDetailsElement).classList.remove('open');
			}
		});
	} else {
		toggled.classList.remove('open');
	}
};
// 분석 버튼 클릭 시 실행 함수
const onClickResearch = async (pk: string) => {
	if (!tokenStore.isLogin) {
		checkLoginDetail();
		return;
	}

	router.push(`/report/kr/${pk}`);
};
// 북마크 적용 후 다시 불러오는 함수
const uploadBookMark = async (pk: string, bookmark: string) => {
	const resAdjustBookmarks = await adjustBookmarks({
		bookmarks: [
			{
				event_pk: pk,
			},
		],
	});
	if (resAdjustBookmarks.data.success) {
		emit('update:bookMarkRefetch', { event_pk: pk, bookmark: bookmark ? 0 : 1 });
	}
};
</script>

<style lang="scss" scoped>
.listItem {
	width: 100%;
	display: flex;
	flex-direction: column;
	background-color: var(--color-bg-mid);
	color: var(--color-base-white);
	border-radius: var(--space-x-small);
	.listItem_summary {
		padding-right: var(--space-mid);
		background-color: var(--color-bg-mid);
		border-radius: var(--space-x-small);
		gap: 0.8rem;
		.listItem_content {
			display: flex;
			width: 100%;
			color: var(--color-base-white);
			white-space: nowrap;
			min-width: 0;
			.bookmark {
				border-radius: var(--space-x-small) 0 0 var(--space-x-small);
				display: flex;
				align-items: center;
				justify-content: center;
				background-color: var(--color-bg-light);
				font-weight: var(--font-w-semi-bold);
				font-size: var(--space-large);
				line-height: 2.387rem;
				padding: 0.8rem;
			}
			.c_inner {
				display: flex;
				justify-content: space-between;
				align-items: center;
				padding: var(--space-mid) 0rem var(--space-mid) 1rem;
				max-width: none;
				min-width: 0;
				.item_pick {
					display: flex;
					align-items: center;
					gap: 0.6rem;
					min-width: 0;
					img {
						width: 3.2rem;
						height: 3.2rem;
						border-radius: 50%;
					}
					.item_title {
						display: flex;
						flex-direction: column;
						min-width: 0;
						span:nth-child(1) {
							display: block;
							white-space: nowrap;
							overflow: hidden;
							text-overflow: ellipsis;
							font-size: var(--font-s-mid);
							color: var(--color-base-white);
							font-weight: var(--font-w-semi-bold);
							line-height: normal;
						}
						span:nth-child(2) {
							display: block;
							white-space: nowrap;
							overflow: hidden;
							text-overflow: ellipsis;
							font-size: var(--font-s-small);
							color: var(--color-base-light);
							font-weight: var(--font-w-light);
							line-height: normal;
						}
						.clip {
							display: flex !important;
							gap: 0.4rem;
							margin-top: 0.4rem;
							img {
								width: 1.4rem;
								height: 1.4rem;
							}
						}
					}
				}
				.item_total {
					color: var(--color-danger);
					font-weight: var(--font-w-semi-bold);
					font-size: var(--font-s-regular);
					line-height: 2.1rem;
				}
			}
		}
	}
	.listItem_bottom {
		display: flex;
		flex-direction: column;
		align-items: center;
		background-color: var(--color-bg-mid);
		border-radius: 0rem 0rem var(--space-x-small) var(--space-x-small);
		padding: var(--space-mid);
		gap: 0.8rem;
		&_content {
			display: flex;
			flex-direction: column;
			justify-content: flex-start;
			align-items: flex-start;
			width: 100%;
			.content_master {
				display: flex;
				align-items: center;
				gap: 1rem;
				img {
					width: 5.8rem;
					height: 5.8rem;
				}
				.master_name {
					font-size: var(--font-s-mid);
					color: var(--color-base-white);
					font-weight: var(--font-w-semi-bold);
					line-height: normal;
				}
			}
		}
		.btn_box {
			display: flex;
			height: 3.2rem;
			.btn-research {
				display: flex;
				padding: 6px 16px;
				justify-content: center;
				align-items: center;
				width: 8rem;
				gap: 1rem;
				font-size: var(--font-s-mid);
				font-weight: var(--font-w-semi-bold);
				line-height: normal;
			}
		}
	}
}
.c_details[open] .listItem_summary .bookmark {
	border-radius: 0.8rem 0 0 0;
}
.c_details[open] .listItem_summary {
	border-radius: var(--space-x-small) var(--space-x-small) 0rem;
}
</style>
